import { Link, useParams } from 'react-router-dom'

import { useLanguage } from '../api/hooks'
import { Button, Card, EmptyState, Spinner } from '../components/ui'

interface GameCard {
  slug: string
  name: string
  blurb: string
  emoji: string
  needsVoice?: boolean
}

const GAMES: GameCard[] = [
  {
    slug: 'match',
    name: 'Match',
    blurb: 'Pair up words against the clock. Wrong pairs cost you time.',
    emoji: '🔗',
  },
  {
    slug: 'speed',
    name: 'Speed round',
    blurb: 'Sixty seconds. As many words as you can manage.',
    emoji: '⚡',
  },
  {
    slug: 'survival',
    name: 'Survival',
    blurb: 'Three lives. Keep going until they run out.',
    emoji: '💀',
  },
  {
    slug: 'sing',
    name: 'Sing along',
    blurb: 'Hear your language read to a beat, and sing it back.',
    emoji: '🎤',
    needsVoice: true,
  },
]

export default function Games() {
  const languageId = Number(useParams().id)
  const { data: language, isPending } = useLanguage(languageId)

  if (isPending) return <Spinner />
  if (!language) return null

  if (language.entry_count === 0) {
    return (
      <EmptyState
        title="No words to play with"
        hint="Games are built from your vocabulary. Add a few words first."
        action={
          <Link to={`/languages/${languageId}`}>
            <Button>Add words</Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/languages/${languageId}/learn`}
          className="text-sm text-muted hover:text-ink"
        >
          ← {language.name}
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">Games</h1>
        <p className="text-sm text-muted">
          Different ways to drill the same words. Everything you answer still
          counts towards your progress.
        </p>
      </div>

      <ul className="grid gap-3 sm:grid-cols-2">
        {GAMES.map((game) => {
          const locked = game.needsVoice && !language.pronunciation_locale
          const target =
            game.slug === 'sing'
              ? `/languages/${languageId}/sing`
              : `/languages/${languageId}/games/${game.slug}`

          return (
            <li key={game.slug}>
              {locked ? (
                <Card className="h-full opacity-60">
                  <GameBody game={game} />
                  <p className="mt-3 text-xs text-muted">
                    Choose a pronunciation voice on the language page to unlock this.
                  </p>
                </Card>
              ) : (
                <Link to={target} className="block h-full">
                  <Card className="h-full transition hover:border-brand">
                    <GameBody game={game} />
                  </Card>
                </Link>
              )}
            </li>
          )
        })}
      </ul>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-medium">Feeling like a break?</h2>
            <p className="text-sm text-muted">
              Spend the tokens you have earned in the arcade.
            </p>
          </div>
          <Link to="/arcade">
            <Button variant="secondary">Open the arcade</Button>
          </Link>
        </div>
      </Card>
    </div>
  )
}

function GameBody({ game }: { game: GameCard }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-3xl" aria-hidden>
        {game.emoji}
      </span>
      <div>
        <h2 className="font-medium">{game.name}</h2>
        <p className="text-sm text-muted">{game.blurb}</p>
      </div>
    </div>
  )
}
